import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Assert } from '@cotera/utilities';
import { useUntenantedClient } from '@cotera/client/app/stores/org';
import { TimeInterval } from '@cotera/api';
import { SummaryCards } from '../summary-cards';
import { WebhookTriggersTable } from '../webhook-triggers-table';
import { atcQueryKeys } from '../query-keys';

interface WebhookTriggersTabProps {
  timeInterval: TimeInterval;
}

export const WebhookTriggersTab: React.FC<WebhookTriggersTabProps> = ({
  timeInterval,
}) => {
  const client = useUntenantedClient();

  const { data, isLoading, error } = useQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getWebhookTriggers({
        timeInterval,
      });
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.webhookTriggers(timeInterval),
  });

  const { data: restateUrlData } = useQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getRestateUrl({});
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.restateUrl(),
  });

  const runs = useMemo(() => data?.runs ?? [], [data]);
  const webhooks = useMemo(() => data?.webhooks ?? [], [data]);
  const registryTruncated = data?.registryTruncated ?? false;

  const { totalSuccess, totalFailures, totalCompleted } = useMemo(() => {
    let success = 0;
    let failures = 0;
    for (const run of runs) {
      if (run.completionResult === 'success') {
        success++;
      } else if (run.completionResult === 'failure') {
        failures++;
      }
    }
    return {
      totalSuccess: success,
      totalFailures: failures,
      totalCompleted: success + failures,
    };
  }, [runs]);

  if (error !== null) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-sm text-red-800 font-medium">
          Failed to load webhook triggers
        </p>
        <p className="text-sm text-red-600">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground">Loading webhook triggers…</p>
    );
  }

  return (
    <div className="space-y-6">
      <SummaryCards
        totalJobs={runs.length}
        successRate={
          totalCompleted > 0 ? (totalSuccess / totalCompleted) * 100 : 0
        }
        totalFailures={totalFailures}
      />

      {registryTruncated && (
        <p className="text-xs text-muted-foreground">
          Showing the most recently created enabled webhooks; some zero-run
          webhooks are not listed. Every webhook with activity in this time
          range is always shown.
        </p>
      )}

      <WebhookTriggersTable
        webhooks={webhooks}
        runs={runs}
        restateUrl={restateUrlData?.restateUrl ?? null}
      />
    </div>
  );
};
